import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useFloating, offset, shift, autoUpdate, flip, useClick, useFocus, useDismiss, useRole, useHover, useInteractions, useMergeRefs, FloatingPortal, FloatingFocusManager } from '@floating-ui/react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { cva, cx } from 'class-variance-authority';
import { useState } from 'react';
import { NavigationBarItem } from '../NavigationBarItem/NavigationBarItem.js';
import { useNavigationContext } from '../NavigationContext/NavigationContext.js';
import { zIndexes } from '../constants.js';
import { navItemTooltipContainer, popoverContainer, popoverItemList } from '../NavigationBarItem/navigation-bar-item-styles.js';

const listStyles = cva("mci-grid mci-transition-opacity mci-duration-default mci-overflow-hidden", {
    variants: {
        isOpen: {
            true: "mci-h-fit mci-visible mci-opacity-100 mci-grid-rows-[min-content_1fr]",
            false: "mci-h-0 mci-invisible mci-opacity-0 mci-grid-rows-[min-content_0fr]",
        },
        isVisible: {
            true: "",
            false: "xs:mci-hidden",
        },
    },
});
const DynamicChevron = ({ isAccordionExpanded, }) => {
    return isAccordionExpanded ? jsx(ChevronUpIcon, {}) : jsx(ChevronDownIcon, {});
};
const NavigationDisclosureItem = ({ icon, label, listItems, }) => {
    const [isDisclosureOpen, setIsDisclosureOpen] = useState(false);
    const { isExpanded: isSideNavExpanded } = useNavigationContext();
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const { refs: popoverRefs, floatingStyles: popoverFloatingStyles, context: popoverContext, } = useFloating({
        open: isPopoverOpen,
        onOpenChange: setIsPopoverOpen,
        middleware: [offset(20), shift({ crossAxis: true })],
        placement: "right",
        whileElementsMounted: autoUpdate,
    });
    const { context: tooltipContext, floatingStyles: tooltipFloatingStyles, refs: tooltipRefs, } = useFloating({
        middleware: [
            flip({
                fallbackAxisSideDirection: "start",
            }),
            offset(20),
            shift(),
        ],
        open: isTooltipOpen,
        onOpenChange: setIsTooltipOpen,
        placement: "right",
        whileElementsMounted: autoUpdate,
    });
    const popoverClick = useClick(popoverContext);
    const popoverFocus = useFocus(popoverContext, { enabled: isPopoverOpen });
    const popoverDismiss = useDismiss(popoverContext);
    const popoverRole = useRole(popoverContext);
    const tooltipDismiss = useDismiss(tooltipContext);
    const tooltipFocus = useFocus(tooltipContext);
    const tooltipHover = useHover(tooltipContext, { move: false });
    const tooltipRole = useRole(tooltipContext, { role: "tooltip" });
    const { getReferenceProps: getTooltipRefProps, getFloatingProps: getTooltipFloatingProps, } = useInteractions([
        tooltipDismiss,
        tooltipFocus,
        tooltipHover,
        tooltipRole,
    ]);
    const { getReferenceProps: getPopoverRefProps, getFloatingProps: getPopoverFloatingProps, } = useInteractions([
        popoverClick,
        popoverFocus,
        popoverDismiss,
        popoverRole,
    ]);
    const mergedRefs = useMergeRefs([
        popoverRefs.setReference,
        tooltipRefs.setReference,
    ]);
    const mergedProps = getTooltipRefProps(getPopoverRefProps({
        onClick: () => {
            if (isSideNavExpanded) {
                setIsDisclosureOpen(!isDisclosureOpen);
            }
        },
    }));
    return (jsxs("ul", { className: "mci-grid mci-grid-cols-1", children: [jsx(NavigationBarItem, { as: "button", "aria-expanded": isSideNavExpanded && isDisclosureOpen, icon: icon, label: label, isExpanded: isSideNavExpanded, endAdornment: isSideNavExpanded ? (jsx(DynamicChevron, { isAccordionExpanded: isDisclosureOpen })) : null, ref: mergedRefs, ...mergedProps }), jsx("ul", { className: listStyles({
                    isOpen: isDisclosureOpen,
                    isVisible: isSideNavExpanded,
                }), "data-testid": "list-container", children: listItems }), isTooltipOpen && !isSideNavExpanded && !isPopoverOpen && (jsx(FloatingPortal, { children: jsx("div", { className: cx(navItemTooltipContainer(), "mci-pl-xs mci-text-gray-800"), ref: tooltipRefs.setFloating, style: {
                        ...tooltipFloatingStyles,
                        zIndex: zIndexes.navigationDialog,
                    }, ...getTooltipFloatingProps(), children: jsx("div", { className: cx("mci-flex mci-items-center"), "data-testid": "tooltip-label", children: label }) }) })), !isSideNavExpanded && isPopoverOpen && (jsx(FloatingPortal, { children: jsx(FloatingFocusManager, { context: popoverContext, modal: false, children: jsxs("div", { className: popoverContainer({ isExpanded: isSideNavExpanded }), "data-testid": "popover-container", ref: popoverRefs.setFloating, style: {
                            ...popoverFloatingStyles,
                            zIndex: zIndexes.navigationDialog,
                        }, ...getPopoverFloatingProps({}), children: [!isSideNavExpanded && (jsxs(Fragment, { children: [jsx("div", { className: "mci-px-s mci-py-xs", children: label }), jsx("div", { className: "mci-mx-0 mci-h-[1px] mci-bg-gray-300" })] })), jsx("ul", { className: cx(popoverItemList(), "mci-px-xs"), children: listItems })] }) }) }))] }));
};

export { NavigationDisclosureItem };
